"use client";

import { ECOLE_DEFAULT_VIDEO, useBackgroundVideo } from "@/hooks/useBackgroundVideo";
import { useEffect, useState } from "react";
import { DeskTutorChicky } from "./DeskTutorChicky";

interface DecorativeBackgroundProps {
  isCanvasVisible: boolean;
  variant?: "default" | "ecole" | "liceo";
  scrollProgress?: number;
  showCinematicOverlay?: boolean;
  showDeskTutor?: boolean;
  isAuthenticated?: boolean;
  media?: { videoUrl: string; posterUrl: string };
}

export function DecorativeBackground({
  isCanvasVisible,
  variant = "default",
  showCinematicOverlay = true,
  showDeskTutor = false,
  isAuthenticated = false,
  media,
}: DecorativeBackgroundProps) {
  const selectedBackground = useBackgroundVideo(
    variant === "ecole"
      ? {
          defaultState: { selectedId: "ecole-default", customUrl: null },
          fallbackVideoUrl: ECOLE_DEFAULT_VIDEO,
        }
      : undefined
  );
  // A school-supplied film is route-owned; never overwrite personal preferences.
  const { posterUrl, videoUrl } = media ?? selectedBackground;
  const [canLoadVideo, setCanLoadVideo] = useState(false);
  const [hasLoadedVideoFrame, setHasLoadedVideoFrame] = useState(false);
  const [isLargeViewport, setIsLargeViewport] = useState(false);
  const [prefersReducedMotion, setPrefersReducedMotion] = useState(false);
  const isEcoleDefaultVideo = videoUrl === ECOLE_DEFAULT_VIDEO;
  const isDesktopMedia = isEcoleDefaultVideo || Boolean(media);
  const canShowSelectedMedia = !isDesktopMedia || isLargeViewport;
  const shouldRenderVideo = Boolean(
    videoUrl && canLoadVideo && canShowSelectedMedia && !prefersReducedMotion
  );
  const shouldShowPoster = Boolean(posterUrl && canShowSelectedMedia && !hasLoadedVideoFrame);

  useEffect(() => {
    const mediaQuery = window.matchMedia("(min-width: 640px)");
    const updateViewport = () => setIsLargeViewport(mediaQuery.matches);

    updateViewport();
    mediaQuery.addEventListener("change", updateViewport);
    return () => mediaQuery.removeEventListener("change", updateViewport);
  }, []);

  useEffect(() => {
    const mediaQuery = window.matchMedia("(prefers-reduced-motion: reduce)");
    const updatePreference = () => setPrefersReducedMotion(mediaQuery.matches);

    updatePreference();
    mediaQuery.addEventListener("change", updatePreference);
    return () => mediaQuery.removeEventListener("change", updatePreference);
  }, []);

  useEffect(() => {
    setCanLoadVideo(false);
    setHasLoadedVideoFrame(false);
    if (!videoUrl || !canShowSelectedMedia || prefersReducedMotion) return;

    const win = window as typeof window & {
      requestIdleCallback?: (callback: IdleRequestCallback, options?: IdleRequestOptions) => number;
      cancelIdleCallback?: (handle: number) => void;
    };
    let timeoutId: number | null = null;
    let idleId: number | null = null;

    const load = () => {
      setCanLoadVideo(true);
    };

    if (typeof win.requestIdleCallback === "function") {
      idleId = win.requestIdleCallback(load, { timeout: 2500 });
    } else {
      timeoutId = window.setTimeout(load, 1500);
    }

    return () => {
      if (idleId !== null && typeof win.cancelIdleCallback === "function") {
        win.cancelIdleCallback(idleId);
      }
      if (timeoutId !== null) {
        window.clearTimeout(timeoutId);
      }
    };
  }, [canShowSelectedMedia, prefersReducedMotion, videoUrl]);
  const mediaWash = variant === "ecole" ? "rgba(7, 19, 29, 0.84)" : "rgba(12, 12, 17, 0.52)";
  const mediaToneStyle = {
    filter: "brightness(0.72) saturate(0.84)",
  };
  const hasBackgroundMedia = Boolean((posterUrl && canShowSelectedMedia) || shouldRenderVideo);
  const shouldShowMobileEcoleBlack = isDesktopMedia && !isLargeViewport;
  const shouldMountDeskTutor = Boolean(
    showDeskTutor &&
    isEcoleDefaultVideo &&
    canShowSelectedMedia &&
    (posterUrl || hasLoadedVideoFrame)
  );

  return (
    <>
      {shouldShowMobileEcoleBlack ? (
        <div className="absolute inset-0 z-0 bg-black" aria-hidden="true" />
      ) : null}

      {hasBackgroundMedia ? (
        <div
          className="absolute inset-0 z-0 overflow-hidden"
          style={{ opacity: isCanvasVisible ? 0 : 0.42 }}
          aria-hidden="true"
        >
          {posterUrl ? (
            <div
              data-testid="background-media-poster"
              className={`absolute inset-0 bg-cover bg-center ${
                shouldShowPoster ? "opacity-100" : "opacity-0"
              }`}
              style={{
                ...mediaToneStyle,
                backgroundImage: `url(${posterUrl})`,
              }}
            />
          ) : null}

          {/* Background video is idle-loaded; the poster above carries first paint. */}
          {shouldRenderVideo ? (
            <video
              key={videoUrl} // Force remount when URL changes
              className="absolute inset-0 h-full w-full object-cover object-[45%_center] sm:object-center"
              src={videoUrl}
              autoPlay
              muted
              loop
              playsInline
              poster={posterUrl ?? undefined}
              preload="metadata"
              onLoadedData={() => setHasLoadedVideoFrame(true)}
              style={mediaToneStyle}
            />
          ) : null}

          <div className="absolute inset-0" style={{ backgroundColor: mediaWash }} />
          <div className="absolute inset-x-0 bottom-0 h-48 bg-gradient-to-t from-black/30 to-transparent" />
        </div>
      ) : null}

      {shouldMountDeskTutor ? (
        <DeskTutorChicky
          isVisible
          reducedMotion={prefersReducedMotion}
          isAuthenticated={isAuthenticated}
        />
      ) : null}

      {showCinematicOverlay && (
        <div
          className={`pointer-events-none fixed inset-0 z-[1] ${isCanvasVisible ? "opacity-0" : "opacity-100"}`}
          aria-hidden="true"
        >
          <div className="absolute inset-x-0 top-0 h-32 bg-gradient-to-b from-black/20 to-transparent" />
          <div className="absolute inset-x-0 bottom-0 h-64 bg-gradient-to-t from-black/20 to-transparent" />
        </div>
      )}
    </>
  );
}
